/* Elin Perfumes — volume tier pricing and cart stepper.
 *
 * Tiers key off the whole order subtotal, so adding any product can move
 * every card into a new tier; one cart fetch therefore repaints every card's
 * hero price, pill and highlighted row, not just the one that was clicked.
 *
 * The stepper is the cart control — there is no Add to Cart button. Clicks
 * update the number immediately and the network call is debounced, so
 * tapping "+" five times sends one absolute-quantity request rather than
 * five racing ones.
 *
 * Elin's listed price carries a per-unit retail premium. At or above the order
 * minimum that premium comes off and the tier percent applies to what remains.
 *
 * Below the self-checkout limit the shown price IS Shopify's price, so checkout
 * can honour it. Above it, checkout is disabled entirely and the quote worker
 * recomputes the identical figure from the same metafield.
 *
 * Every threshold is measured at LIST price, as the quote worker measures it.
 * Once the premium is a native automatic discount, Shopify's own subtotal
 * already has it off, and reading that would both move the checkout/quote line
 * and take the premium off a second time.
 */
(() => {
  const configEl = document.getElementById('elin-tier-config');
  if (!configEl) return;

  let config;
  try {
    config = JSON.parse(configEl.textContent);
  } catch {
    return;
  }

  /* Published by wmafusion-product-sync to shop.metafields.elinperfumes.volume_tiers.
     No ladder means no tier UI at all -- better blank than a price we will not honour. */
  const ladder = config.ladder || null;
  const tiers = (ladder && ladder.tiers) || [];
  const minimum = Number(ladder && ladder.minimum) || 0;
  const premiumFlat = Math.round((Number(ladder && ladder.premium && ladder.premium.flat) || 0) * 100);
  const premiumPct = Number(ladder && ladder.premium && ladder.premium.pct) || 0;

  const root = window.Shopify?.routes?.root || '/';

  /* product-sync publishes checkoutMax in the same run that creates the native
     automatic discount for the premium. From then on checkout charges the premium-off
     price itself, so the line is the ladder's and the premium shows from the minimum.
     Capped at the first tier: checkout carries no tier percent, so a checkout order
     must never be shown one. Until it is published, the theme setting and the old
     rule stand -- the premium only came off for orders that left as a quote. */
  const nativeMax = Number(ladder && ladder.checkoutMax) || 0;
  const firstTier = Math.min(...tiers.map((t) => Number(t.threshold) || Infinity));
  const selfCheckoutMax = nativeMax
    ? Math.min(nativeMax, firstTier)
    : Number(config.selfCheckoutMax) || 0;
  const premiumFloor = nativeMax ? minimum : Math.max(minimum, selfCheckoutMax);

  /** The cart at list price, before any discount Shopify has already taken. */
  const cartList = (cart) =>
    cart.original_total_price ?? cart.items_subtotal_price ?? cart.total_price ?? 0;
  const premiumOff = (cents) => premiumFloor > 0 && cents >= premiumFloor * 100;

  /** Per-unit premium in cents. `pct` is 0 for Elin today; kept so the shape is honoured. */
  const premiumFor = (listCents) => premiumFlat + Math.round(listCents * premiumPct / 100);

  /** Unit price: premium off first, then the tier percent on what remains. */
  const unitPrice = (listCents, pct, dropPremium) => {
    const net = dropPremium ? Math.max(0, listCents - premiumFor(listCents)) : listCents;
    return Math.round(net * (1 - (pct || 0) / 100));
  };

  /* Always en-US. Formatting by the page language gave Spain's layout on /es --
     "350,00 US$" -- beside the "$350.00" Shopify's own money filter prints on the
     same page. Elin sells in USD to the US, Mexico, Central America and the
     Caribbean, which all write it the US way, so one format serves every locale. */
  const money = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: config.currency || 'USD',
  });
  const fmt = (cents) => money.format(cents / 100);

  /* Visible labels come from the theme's language files through elin-tier-config,
     so /es reads Spanish. The English here is only a fallback for a stale config. */
  const L = Object.assign({
    yourPrice: 'Your price', volumePricing: 'Volume pricing',
    wholesalePricing: 'Wholesale pricing', standardPricing: 'Standard pricing',
    itemOne: 'item', itemOther: 'items', youSave: 'You save %amount%',
    addMore: 'Add %amount% more to reach %tier%', bestReached: 'Best pricing reached',
  }, config.i18n || {});
  const label = (s, vars) => s.replace(/%(\w+)%/g, (m, k) => (k in vars ? vars[k] : m));

  /** Whole-dollar threshold label, e.g. "$450+". */
  const tierLabel = (tier) =>
    `${money.format(tier.threshold).replace(/[.,]00$/, '')}+`;

  /** Index of the highest tier the subtotal reaches, or -1 for none.
      Measured on the subtotal the buyer sees, not the post-premium figure. */
  const tierIndexFor = (subtotalCents) => {
    let index = -1;
    tiers.forEach((tier, i) => {
      if (subtotalCents >= tier.threshold * 100) index = i;
    });
    return index;
  };

  /* ---------- painting ---------- */

  function paintCards(cart, activeIndex) {
    const subtotal = cartList(cart);
    const dropPremium = premiumOff(subtotal);
    const pct = activeIndex >= 0 ? tiers[activeIndex].percent : 0;
    const activeRow = activeIndex >= 0 ? activeIndex : dropPremium ? -0.5 : -1;

    const qtyByVariant = new Map();
    (cart.items || []).forEach((item) => {
      qtyByVariant.set(String(item.variant_id), item.quantity);
    });

    document.querySelectorAll('[data-elin-card]').forEach((card) => {
      const base = Number(card.dataset.elinBase);
      const variant = card.dataset.elinVariant;

      const payable = unitPrice(base, pct, dropPremium);
      const price = card.querySelector('[data-elin-card-price]');
      if (price && base) {
        price.textContent = fmt(payable);
      }

      // Struck list price appears only when it differs from what they pay.
      const was = card.querySelector('[data-elin-card-was]');
      if (was && base) was.hidden = payable >= base;

      const pill = card.querySelector('[data-elin-card-pill]');
      if (pill) {
        if (pct || dropPremium) {
          pill.hidden = false;
          pill.textContent = pct
            ? `${L.yourPrice} · ${tierLabel(tiers[activeIndex])}`
            : L.yourPrice;
        } else {
          pill.hidden = true;
        }
      }

      /* Which row is "you are here". activeIndex is the TIER index, so it is -1 for
         any cart below the first tier -- including one that has cleared the order
         minimum and earned the premium removal. Without the -0.5 case that cart
         highlights "Under $200" while its price already reflects the premium coming
         off, which contradicts the row it points at. */
      card.querySelectorAll('[data-elin-tier-row]').forEach((row) => {
        const band = Number(row.dataset.elinTierRow);
        row.classList.toggle('now', band === activeRow);
        // Levels already passed are struck out, so the table shows where you are on
        // the ladder rather than presenting every level as equally available.
        row.classList.toggle('past', band < activeRow);
      });

      // Skip while a click is still settling, so the optimistic number stands.
      const qtyEl = card.querySelector('[data-elin-qty]');
      if (qtyEl && !pending.has(variant)) {
        qtyEl.textContent = String(qtyByVariant.get(variant) || 0);
      }
    });
  }

  /* Above the self-checkout limit the order leaves as a quote, so every
     way of paying has to go — the cart button, the wallet buttons and the
     product-page express buttons alike. Driven off the live subtotal
     because the cart changes over AJAX after Liquid has rendered. */
  function paintCheckoutMode(cart) {
    if (!selfCheckoutMax) return;
    const subtotal = cartList(cart);
    const quoteMode = cart.item_count > 0 && subtotal >= selfCheckoutMax * 100;
    document.documentElement.classList.toggle('elin-quote-mode', quoteMode);
    document.querySelectorAll('.elin-quote-cta').forEach((el) => {
      el.hidden = !quoteMode;
    });
  }

  /* The bar is fixed to the bottom of the viewport, so anything else that sits at the
     bottom has to be told how much room to leave: the page footer, and the cart
     drawer's sticky summary -- which is where the wallet buttons live. Measured rather
     than hard-coded, because the bar wraps to two rows on a narrow screen and a fixed
     84px left the wallet buttons half-covered on a phone, which is most retail traffic. */
  let barObserver = null;
  function measureBar(bar) {
    const publish = () => {
      const h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--elin-bar-h', h + 'px');
    };
    publish();
    if (!barObserver && typeof ResizeObserver !== 'undefined') {
      barObserver = new ResizeObserver(publish);
      barObserver.observe(bar);
    }
  }

  function paintBar(cart, activeIndex) {
    const bar = document.querySelector('[data-elin-bar]');
    if (!bar) return;

    const showing = Boolean(cart.item_count);
    bar.hidden = !showing;
    document.documentElement.classList.toggle('elin-has-bar', showing);
    if (!showing) {
      document.documentElement.style.removeProperty('--elin-bar-h');
      return;
    }
    measureBar(bar);

    const subtotal = cartList(cart);
    const pct = activeIndex >= 0 ? tiers[activeIndex].percent : 0;
    const dropPremium = premiumOff(subtotal);

    /* Start from list, whatever Shopify has already taken off, and recompute what
       the buyer actually pays the same way the quote worker will. Below the checkout
       line that is also what Shopify's own discount charges. The saving is stated
       against list. */
    const premiumTotal = dropPremium
      ? premiumFlat * (cart.item_count || 0) + Math.round(subtotal * premiumPct / 100)
      : 0;
    const payable = Math.round(Math.max(0, subtotal - premiumTotal) * (1 - pct / 100));
    const saving = Math.max(0, subtotal - payable);

    const set = (name, value) => {
      const el = bar.querySelector(`[data-elin-bar-${name}]`);
      if (el) el.textContent = value;
    };

    const cta = bar.querySelector('.elin-bar__checkout');
    if (cta && selfCheckoutMax) {
      const quoteMode = subtotal >= selfCheckoutMax * 100;
      cta.textContent = quoteMode ? `${config.quoteLabel || 'Request a quote'} →` : cta.dataset.elinDefaultLabel || cta.textContent;
      cta.href = quoteMode && config.quoteUrl ? config.quoteUrl : `${root}cart`;
    }

    set(
      'tier',
      pct ? `${L.volumePricing} · ${tierLabel(tiers[activeIndex])}`
          : dropPremium ? L.wholesalePricing
          : L.standardPricing
    );
    set('total', fmt(payable));
    // Struck list total, shown only when the buyer is actually paying less than list.
    const wasEl = bar.querySelector('[data-elin-bar-was]');
    if (wasEl) {
      wasEl.hidden = saving <= 0;
      wasEl.textContent = saving > 0 ? fmt(subtotal) : '';
    }
    set('count', `· ${cart.item_count} ${cart.item_count === 1 ? L.itemOne : L.itemOther}`);
    // Only ever stated when there is one, measured from Elin's list price.
    set('savings', saving > 0 ? label(L.youSave, { amount: fmt(saving) }) : '');

    const next = tiers[activeIndex + 1];
    const fill = bar.querySelector('[data-elin-bar-fill]');
    if (next) {
      const floor = activeIndex >= 0 ? tiers[activeIndex].threshold * 100 : 0;
      const ceiling = next.threshold * 100;
      const progress = Math.min(100, Math.max(3, Math.round(((subtotal - floor) / (ceiling - floor)) * 100)));
      if (fill) fill.style.width = `${progress}%`;
      set('hint', label(L.addMore, { amount: fmt(ceiling - subtotal), tier: tierLabel(next) }));
    } else {
      if (fill) fill.style.width = '100%';
      set('hint', L.bestReached);
    }
  }

  /* ---------- cart state ---------- */

  let inFlight = null;

  async function refresh() {
    if (inFlight) return inFlight;
    inFlight = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((cart) => {
        if (!cart) return;
        const index = tierIndexFor(cartList(cart));
        paintCards(cart, index);
        paintBar(cart, index);
        paintCheckoutMode(cart);
      })
      .catch(() => {})
      .finally(() => {
        inFlight = null;
      });
    return inFlight;
  }

  /* ---------- stepper writes ---------- */

  const pending = new Map();
  let flushTimer = null;

  async function flush() {
    if (!pending.size) return;
    const updates = Object.fromEntries(pending);
    pending.clear();
    try {
      const res = await fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates }),
      });
      if (res.ok) document.dispatchEvent(new CustomEvent('cart:lines-update', { bubbles: true }));
    } catch {
      /* the refresh below re-reads the real cart either way */
    }
    refresh();
  }

  document.addEventListener('click', (event) => {
    const step = event.target.closest('[data-elin-step]');
    if (!step) return;

    const card = step.closest('[data-elin-card]');
    const qtyEl = card?.querySelector('[data-elin-qty]');
    const variant = card?.dataset.elinVariant;
    if (!qtyEl || !variant) return;

    event.preventDefault();
    const next = Math.max(0, Number(qtyEl.textContent || 0) + Number(step.dataset.elinStep));
    qtyEl.textContent = String(next);
    pending.set(variant, next);

    clearTimeout(flushTimer);
    flushTimer = setTimeout(flush, 350);
  });

  /* ---------- stay in sync with the rest of the theme ---------- */

  ['cart:lines-update', 'cart:update', 'cart:refresh'].forEach((name) =>
    document.addEventListener(name, () => {
      if (!pending.size) refresh();
    })
  );
  window.addEventListener('pageshow', () => refresh());

  refresh();
})();
